/**
 * PG사 등록/수정 폼 페이지
 */

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Form, Spinner, Row, Col } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import { pgService } from '../../services/pgService';
import { PgProvider } from '../../types/pg';

interface PgProviderFormData {
  provider_name: string;
  provider_code: string;
  api_key?: string;
  api_secret?: string;
  commission_rate: number;
  status: 'active' | 'inactive';
}

const PgProviderForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const providerId = id && id !== 'new' ? parseInt(id, 10) : 0;
  const isEdit = !!providerId && providerId > 0;

  const { register, handleSubmit, formState: { errors }, reset } = useForm<PgProviderFormData>({
    defaultValues: {
      status: 'active',
      commission_rate: 0,
    },
  });

  const { data: provider, isLoading } = useQuery({
    queryKey: ['pgProvider', providerId],
    queryFn: () => pgService.getProvider(providerId),
    enabled: isEdit,
  });

  useEffect(() => {
    if (provider && isEdit) {
      reset({
        provider_name: provider.provider_name,
        provider_code: provider.provider_code,
        api_key: provider.api_key || '',
        api_secret: provider.api_secret || '',
        commission_rate: provider.commission_rate,
        status: provider.status,
      });
    }
  }, [provider, isEdit, reset]);

  const createMutation = useMutation({
    mutationFn: (data: Partial<PgProvider>) => {
      const formData = { ...data };
      formData.commission_rate = Number(formData.commission_rate);
      return pgService.createProvider(formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pgProviders'] });
      navigate('/admin/pg/providers');
    },
    onError: (error: any) => {
      alert(`등록 실패: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<PgProvider>) => {
      const formData = { ...data };
      formData.commission_rate = Number(formData.commission_rate);
      return pgService.updateProvider(providerId, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pgProviders'] });
      queryClient.invalidateQueries({ queryKey: ['pgProvider', providerId] });
      navigate(`/admin/pg/providers/${providerId}`);
    },
    onError: (error: any) => {
      alert(`수정 실패: ${error.message}`);
    },
  });

  const onSubmit = (data: PgProviderFormData) => {
    const formData: Partial<PgProvider> = {
      ...data,
    };

    if (isEdit) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>{isEdit ? 'PG사 수정' : 'PG사 등록'}</h1>
          <Button variant="secondary" onClick={() => navigate('/admin/pg/providers')}>
            목록으로
          </Button>
        </div>

        <Card>
          <Card.Body>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>PG사명 <span style={{ color: '#ff6b6b' }}>*</span></Form.Label>
                    <Form.Control
                      type="text"
                      {...register('provider_name', { required: 'PG사명을 입력해주세요.' })}
                      isInvalid={!!errors.provider_name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.provider_name?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>PG사 코드 <span style={{ color: '#ff6b6b' }}>*</span></Form.Label>
                    <Form.Control
                      type="text"
                      {...register('provider_code', { required: 'PG사 코드를 입력해주세요.' })}
                      isInvalid={!!errors.provider_code}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.provider_code?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>API Key</Form.Label>
                    <Form.Control
                      type="text"
                      {...register('api_key')}
                      placeholder="PG사 API Key"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>API Secret</Form.Label>
                    <Form.Control
                      type="password"
                      {...register('api_secret')}
                      placeholder="PG사 API Secret"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>수수료율 (%) <span style={{ color: '#ff6b6b' }}>*</span></Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      {...register('commission_rate', { 
                        required: '수수료율을 입력해주세요.',
                        min: { value: 0, message: '0 이상의 값을 입력해주세요.' },
                        max: { value: 100, message: '100 이하의 값을 입력해주세요.' },
                      })}
                      isInvalid={!!errors.commission_rate}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.commission_rate?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>상태 <span style={{ color: '#ff6b6b' }}>*</span></Form.Label>
                    <Form.Select
                      {...register('status', { required: '상태를 선택해주세요.' })}
                      isInvalid={!!errors.status}
                    >
                      <option value="active">활성</option>
                      <option value="inactive">비활성</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.status?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={() => navigate('/admin/pg/providers')}>
                  취소
                </Button>
                <Button variant="primary" type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      {isEdit ? '수정 중...' : '등록 중...'}
                    </>
                  ) : (
                    isEdit ? '수정' : '등록'
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default PgProviderForm;

